// src/pages/VnbPicker.jsx
// VNB-Auswahl mit Suchfeld + Favoriten + Anlegen-Button.
// Loest #13 (+ Neuer VNB Button) und #15 (Suche + Favoriten).

import { useState, useMemo } from 'react';
import { db } from '../lib/supabase';
import { C, Field, TextInput, Row } from './formui';

// 15.05.: Karlsruhe-Region-Favoriten (Anna-Lena). Namensteile, nach denen wir matchen.
// VNBs mit diesen Namen werden im Dropdown ganz oben angezeigt.
const FAVORITEN_KEYWORDS = ['netze bw', 'stadtwerke karlsruhe', 'stadtwerke ettlingen'];

function isFavorit(vnb) {
  const n = (vnb.name || '').toLowerCase();
  return FAVORITEN_KEYWORDS.some(k => n.includes(k));
}

export default function VnbPicker({ vnben, selectedId, onChange, onCreated }) {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newVnb, setNewVnb] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // 17.05. #47: Wenn ein VNB ausgewaehlt ist, Liste zugeklappt. Klick auf "Anderen waehlen"
  // expandiert wieder. Wenn nichts ausgewaehlt, Liste expandiert wie vorher.
  const [forceExpanded, setForceExpanded] = useState(false);

  // Gefilterte Liste: erst Favoriten, dann nach Such-Begriff filtern.
  const filtered = useMemo(() => {
    const list = vnben || [];
    const q = search.trim().toLowerCase();
    const sorted = [...list].sort((a, b) => {
      const af = isFavorit(a), bf = isFavorit(b);
      if (af && !bf) return -1;
      if (!af && bf) return 1;
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
    if (!q) return sorted;
    return sorted.filter(v =>
      (v.name || '').toLowerCase().includes(q) ||
      (v.ort || '').toLowerCase().includes(q) ||
      (v.bundesland || '').toLowerCase().includes(q)
    );
  }, [vnben, search]);

  const selectedVnb = (vnben || []).find(v => v.id === selectedId);

  const startCreate = () => {
    setSaveError(null);
    setNewVnb({
      name: search.trim(), // wenn schon was getippt: als Default-Name
      strasse: '', plz: '', ort: '', bundesland: '',
      telefon_zentrale: '', ansprechpartner: '', position: '',
      telefon_durchwahl: '', mobil: '', ansprechpartner_email: '',
      email: '', webseite: '', anmerkung: '',
    });
    setCreating(true);
  };
  const cancelCreate = () => { setCreating(false); setNewVnb(null); setSaveError(null); };
  const setNew = (k, v) => setNewVnb(prev => ({ ...prev, [k]: v }));

  const saveNew = async () => {
    if (!newVnb?.name?.trim()) {
      setSaveError('Bitte VNB-Name eingeben.');
      return;
    }
    setSaving(true); setSaveError(null);
    try {
      const payload = { ...newVnb, name: newVnb.name.trim() };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const { data, error } = await db.vnb.save(payload);
      if (error) throw error;
      if (!data) throw new Error('Keine Antwort vom Server.');
      // Neu angelegten VNB automatisch auswaehlen + Liste reloaden
      if (onCreated) onCreated(data);
      onChange(data.id);
      setCreating(false);
      setNewVnb(null);
      setSearch('');
    } catch (e) {
      setSaveError(e.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  if (creating && newVnb) {
    return (
      <div style={{ padding: 14, background: C.yellowBg, borderRadius: 8, border: '2px solid ' + C.yellow }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, marginBottom: 10 }}>+ Neuen VNB anlegen</div>
        <Field label='Energieversorger (Name)' required>
          <TextInput value={newVnb.name} onChange={v => setNew('name', v)}/>
        </Field>
        <Row columns={3}>
          <Field label='Strasse'><TextInput value={newVnb.strasse} onChange={v => setNew('strasse', v)}/></Field>
          <Field label='PLZ'><TextInput value={newVnb.plz} onChange={v => setNew('plz', v)}/></Field>
          <Field label='Ort'><TextInput value={newVnb.ort} onChange={v => setNew('ort', v)}/></Field>
        </Row>
        <Row columns={2}>
          <Field label='Bundesland'><TextInput value={newVnb.bundesland} onChange={v => setNew('bundesland', v)}/></Field>
          <Field label='Telefon Zentrale'><TextInput value={newVnb.telefon_zentrale} onChange={v => setNew('telefon_zentrale', v)}/></Field>
        </Row>
        <Row columns={2}>
          <Field label='E-Mail'><TextInput value={newVnb.email} onChange={v => setNew('email', v)} type='email'/></Field>
          <Field label='Webseite'><TextInput value={newVnb.webseite} onChange={v => setNew('webseite', v)} type='url'/></Field>
        </Row>
        <div style={{ fontSize: 13, color: C.gray500, fontStyle: 'italic', marginTop: 6, marginBottom: 6 }}>
          Detaillierte Ansprechpartner-Daten kannst du spaeter unter "Admin -&gt; VNBs verwalten" pflegen.
        </div>
        {saveError && (
          <div style={{ padding: 10, background: C.redBg, color: C.red, borderRadius: 6, fontSize: 14, marginBottom: 10 }}>! {saveError}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancelCreate} disabled={saving} style={{ padding: '8px 14px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>Abbrechen</button>
          <button onClick={saveNew} disabled={saving} style={{ padding: '8px 16px', background: saving ? C.gray300 : C.yellow, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>{saving ? 'Speichert...' : 'VNB anlegen'}</button>
        </div>
      </div>
    );
  }

  // Wenn VNB ausgewaehlt und nicht explizit aufgeklappt: kompakte Anzeige.
  if (selectedVnb && !forceExpanded) {
    return (
      <div style={{ padding: 12, background: C.gray100, borderRadius: 8, fontSize: 15, color: C.gray700, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          Ausgewaehlt: <strong style={{ color: C.navy }}>{selectedVnb.name}</strong>
          {selectedVnb.ort && <span style={{ color: C.gray500 }}> ({selectedVnb.ort})</span>}
          {selectedVnb.ansprechpartner && <div style={{ fontSize: 13, color: C.gray500, marginTop: 4 }}>Ansprechpartner: {selectedVnb.ansprechpartner}{selectedVnb.telefon_durchwahl ? ' - Tel ' + selectedVnb.telefon_durchwahl : ''}</div>}
        </div>
        <button type='button' onClick={() => setForceExpanded(true)} style={{ padding: '6px 12px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Anderen VNB waehlen</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <input
          type='text'
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='VNB suchen (Name, Ort, Bundesland)...'
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 7, border: '1.5px solid ' + C.gray200, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
        />
        <button type='button' onClick={startCreate} style={{ padding: '8px 14px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>+ Neuer VNB</button>
      </div>
      <div style={{ maxHeight: 240, overflowY: 'auto', border: '1.5px solid ' + C.gray200, borderRadius: 7, background: C.white }}>
        <div
          onClick={() => onChange(null)}
          style={{
            padding: '10px 12px', cursor: 'pointer', fontSize: 15, color: C.gray500,
            background: !selectedId ? C.yellowBg : 'transparent',
            borderBottom: '1px solid ' + C.gray100,
          }}>-- Kein VNB ausgewählt --</div>
        {filtered.length === 0 ? (
          <div style={{ padding: 14, fontSize: 15, color: C.gray500, fontStyle: 'italic' }}>
            Keine VNBs gefunden{search ? ' für "' + search + '"' : ''}. Mit "+ Neuer VNB" anlegen.
          </div>
        ) : filtered.map(v => {
          const selected = selectedId === v.id;
          const fav = isFavorit(v);
          return (
            <div
              key={v.id}
              onClick={() => { onChange(v.id); setForceExpanded(false); setSearch(''); }}
              style={{
                padding: '10px 12px', cursor: 'pointer', fontSize: 15,
                background: selected ? C.yellowBg : 'transparent',
                borderBottom: '1px solid ' + C.gray100,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
              {fav && <span style={{ background: C.yellow, color: C.navy, padding: '1px 6px', borderRadius: 10, fontSize: 12, fontWeight: 800 }}>Favorit</span>}
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, color: C.navy }}>{v.name}</span>
                {v.ort && <span style={{ color: C.gray500, marginLeft: 6 }}>({v.ort})</span>}
              </div>
              {selected && <span style={{ color: C.green, fontWeight: 800, fontSize: 14 }}>OK</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
